/* eslint-disable */
import type { FunctionComponent, HTMLProps, MouseEventHandler } from 'react'
import React from 'react'
import { Link } from '../../Link'

/**
 * This component is to be used in place of plain in-page links (aka anchor links or hash links)
 * which don't work in WCD in Safari.
 * Inspired by https://github.com/rafgraph/react-router-hash-link
 * The component is only set up for linking to non-interactive elements e.g. headings
 * If interactive elements need to be supported in future, see above project for logic to not set
 * the tabindex on interactive elements
 *
 * TODO: smooth scrolling - https://whichonline.atlassian.net/browse/CTX-1354
 */
export const InPageAnchorLink: FunctionComponent<InPageAnchorLinkProps> = ({
  href,
  children,
  onClick,
  ...rest
}) => {
  /*
   * As well as scrolling, the browser built-in anchor link functionality moves the
   * 'sequential focus navigation point' to the target.
   * This allows keyboard & screen reader users to keep tabbing through the page.
   * There is not yet an API to set this specifically, so instead we focus the element and then blur
   * it to remove the focus outline.
   * To focus a non-keyboard focusable element we have to set a tabindex, then we remove afterwards
   * See https://github.com/whatwg/html/issues/5326
   * and https://github.com/rafgraph/react-router-hash-link
   * */
  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    // Prevent browser in-page link behaviour
    e.preventDefault()

    if (typeof onClick === 'function') {
      onClick(e)
    }

    // React router not used because it causes a rerender, which leads to inconsistent behaviour
    window.history.pushState(null, '', href)

    const linkTargetElement = document.getElementById(href.replace('#', ''))
    const shouldJumpToTop = !linkTargetElement && href === '#top'

    const element = shouldJumpToTop ? document.body : linkTargetElement

    if (element) {
      const isKeyboardFocusable = element.getAttribute('tabindex') !== null

      if (!isKeyboardFocusable) {
        element.setAttribute('tabindex', '-1')
      }

      // Required regardless of smooth scrolling because Safari does not auto scroll when focusing
      element.scrollIntoView()

      element.focus({ preventScroll: true })
      element.blur()

      if (!isKeyboardFocusable) {
        element.removeAttribute('tabindex')
      }
    }
  }

  return (
    <Link href={href} onClick={handleClick} {...rest}>
      {children}
    </Link>
  )
}

///////// IMPLEMENTATION /////////

type InPageAnchorLinkProps = {
  href: `#${string}`
  children: React.ReactNode
} & Exclude<Parameters<typeof Link>[0], 'href'> &
  HTMLProps<HTMLAnchorElement>
